import { useEffect, useState } from 'react';
import { NAV_ITEMS } from '@/data/mockData';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { logout, loadSession } from '@/lib/authApi';
import { flushSyncBeforeLogout } from '@/lib/syncEngine';
import { AppleCardAlert } from '@/components/AppleCardAlert';
import { BrandLogo } from '@/components/BrandLogo';
import { getVisibleNavItems, usePermissionRevision } from '@/lib/permissions';
import { LogOut } from 'lucide-react';

const ROLE_LABELS: Record<string, string> = {
  boss: '老板', manager: '经理', sales: '业务员', retail: '零售',
  driver: '司机', writer: '写单员', shopkeeper: '店面',
};
import {
  LayoutDashboard, Database, Truck, Package, ShoppingCart,
  Wallet, MapPin, Barcode, TrendingUp, Settings, Sparkles,
  ScanLine, Users, Warehouse, PackagePlus, ListOrdered, History,
  Layers, PackageMinus, AlertTriangle, Receipt, FileText, Undo2,
  TrendingDown, Scale, Map, Route, LayoutTemplate, DollarSign,
  UserCog, Shield, Activity, ClipboardList,
  Lock, ArchiveRestore, Tag, Cloud, CloudCog, X, PackageCheck, StickyNote, ImagePlus, Printer,
} from 'lucide-react';

const ICON_MAP: Record<string, React.ComponentType<{ className?: string; strokeWidth?: number }>> = {
  LayoutDashboard, Database, Truck, Package, ShoppingCart,
  Wallet, MapPin, Barcode, TrendingUp, Settings, Sparkles,
  ScanLine, Users, Warehouse, PackagePlus, ListOrdered, History,
  Layers, PackageMinus, AlertTriangle, Receipt, FileText, Undo2,
  TrendingDown, Scale, Map, Route, LayoutTemplate, DollarSign,
  UserCog, Shield, Activity, ClipboardList, Lock, ArchiveRestore, Tag, Cloud, CloudCog, PackageCheck, StickyNote, ImagePlus, Printer,
};

interface MobileSidebarDrawerProps {
  open: boolean;
  currentPage: string;
  activeChildId?: string;
  onClose: () => void;
  onNavigate: (page: string, childId?: string) => void;
}

export function MobileSidebarDrawer({
  open,
  currentPage,
  activeChildId,
  onClose,
  onNavigate,
}: MobileSidebarDrawerProps) {
  const { user, logoutNow } = useAuth();
  usePermissionRevision();
  const visibleNavItems = getVisibleNavItems(user?.role);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const handleLogout = async () => {
    setConfirmLogout(false);
    const token = loadSession()?.token;
    // 尽力上传，但退出绝不能因 rejected-stale、网络抖动或超时要求用户重复操作。
    // 被拒绝的旧本地项已由同步引擎撤销快照，下一次登录会以服务器版本为准。
    const syncResult = await flushSyncBeforeLogout();
    if (!syncResult.ok) console.warn('[logout] sync incomplete:', syncResult.error);
    if (token) { try { await logout(token); } catch { /* 本机仍须退出，令牌到期后也不可再用。 */ } }
    onClose();
    logoutNow();
  };

  const displayName = user?.displayName || user?.username || '未登录';
  const roleLabel = ROLE_LABELS[user?.role || ''] || '';
  const avatarChar = displayName.slice(0, 1);

  // ESC 关闭
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // 打开时锁滚动
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const isItemActive = (item: typeof NAV_ITEMS[number]) => {
    if (currentPage === item.path) return true;
    if (item.children?.some((c) => c.path === currentPage || activeChildId === c.id)) return true;
    return false;
  };
  const isChildActive = (child: { id: string; path: string }) => {
    return activeChildId === child.id || (activeChildId === undefined && currentPage === child.path);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[200] xl:hidden" aria-modal="true" role="dialog">
      {/* 背景遮罩 */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* 抽屉主体 */}
      <aside className="absolute left-0 top-0 bottom-0 w-[78vw] max-w-[320px] bg-[#0f172a] flex flex-col shadow-2xl" style={{ transform: 'translateX(0)', transition: 'transform 200ms ease-out' }}>
        {/* Logo 头 */}
        <div className="h-16 px-4 flex items-center justify-between border-b border-white/10 flex-shrink-0">
          <div className="flex items-center gap-3">
            <BrandLogo size={40} rounded="rounded-xl" />
            <div className="text-white">
              <div className="text-[14px] font-semibold">昌发工作台</div>
              <div className="text-[11px] text-slate-400">昌发香行</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-lg text-slate-400 hover:bg-white/5 hover:text-white flex items-center justify-center"
            aria-label="关闭菜单"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 菜单列表 */}
        <nav className="flex-1 overflow-y-auto py-2 px-2 scrollbar-thin">
          {visibleNavItems.map((item) => {
            const Icon = ICON_MAP[item.icon] || LayoutDashboard;
            const active = isItemActive(item);
            const hasChildren = !!(item.children && item.children.length > 0);

            return (
              <div key={item.id} className="mb-1">
                <button
                  onClick={() => {
                    if (!hasChildren) {
                      onNavigate(item.path);
                      onClose();
                    }
                  }}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-3 rounded-lg text-[14px] leading-[1.3] tracking-[0.02em] transition-colors',
                    active
                      ? 'bg-emerald-600/15 text-emerald-400 font-medium'
                      : 'text-slate-200 hover:bg-white/5'
                  )}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.75} />
                  <span
                    className="flex-1 text-left font-medium"
                    style={{ textShadow: '0 0 1px rgba(255,255,255,0.08)' }}
                  >
                    {item.label}
                  </span>
                </button>

                {/* 二级菜单始终展开（抽屉里不嵌套 hover） */}
                {hasChildren && (
                  <div className="ml-9 mt-1 mb-2 space-y-0.5 border-l border-white/5 pl-2">
                    {item.children!.map((child) => {
                      const ChildIcon = ICON_MAP[child.icon] || LayoutDashboard;
                      const childActive = isChildActive(child);
                      return (
                        <button
                          key={child.id}
                          onClick={() => {
                            onNavigate(child.path, child.id);
                            onClose();
                          }}
                          className={cn(
                            'w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] transition-colors',
                            childActive
                              ? 'bg-emerald-600/20 text-emerald-300 font-medium'
                              : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
                          )}
                        >
                          <ChildIcon className="w-4 h-4 flex-shrink-0" strokeWidth={1.75} />
                          <span className="flex-1 text-left">{child.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* 底部用户 */}
        <div className="p-3 border-t border-white/10 flex-shrink-0">
          <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/5">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-600 to-emerald-800 flex items-center justify-center text-white text-[12px] font-medium">
              {avatarChar}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium text-white truncate">{displayName}</div>
              <div className="text-[11px] text-slate-400 truncate">{roleLabel || '昌发佛具'}</div>
            </div>
            <button
              type="button"
              onClick={() => setConfirmLogout(true)}
              title="退出登录"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-red-400 hover:bg-white/10 transition"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      <AppleCardAlert
        open={confirmLogout}
        type="confirm"
        title="退出登录"
        message={`确定要退出「${displayName}」吗？本机数据会保留，下次登录后继续同步。`}
        confirmText="退出"
        cancelText="取消"
        onClose={() => setConfirmLogout(false)}
        onConfirm={handleLogout}
      />
    </div>
  );
}
